import { BybImageSection } from "@Components/common/byb-ds";
import { LegacyContentWrapper } from "@Components/game";
import SessionResultsLoadingOverlay from "@Components/game/common/session-results-loading-overlay";
import PlaylistDescription from "@Components/game/playlists/playlist/playlist-description";
import PlaylistPerformance from "@Components/game/playlists/playlist/playlist-performance";
import PlaylistResult from "@Components/game/playlists/playlist/playlist-result";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import usePlaylistData from "@hooks/game/playlists/use-playlist-data";
import { Container, Grid, Paper, Stack, useTheme } from "@mui/material";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

const PageId = "PLAYLIST";

const Playlist = () => {
  const theme = useTheme();
  const { playlistId } = useParams();
  const { state, dispatch } = useContext(AppContext);
  const { playlist, loading, bestResult, currentStats, previousStats } = usePlaylistData({ id: +playlistId! });

  const handleViewAllResults = () => {
    Events.app.navigate.emit({
      path: `${location.pathname}/sessions`,
      data: {
        playlist_id: +playlistId!,
      },
    });
  };

  const startSession = () => {
    dispatch({
      type: Actions.SessionActions.CreatePlaylistSession,
      payload: {
        account_id: state.account?.id || null,
        playlist_id: +playlistId!,
      },
      onSuccess: onPlaylistSessionCreated,
    });
  };

  const onPlaylistSessionCreated = (response: any) => {
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.StartPlaylist,
      payload: {
        items: response.play_items,
        play_session_id: response.play_session_id,
      },
    });
  };

  const navigateToSessionResults = (playSessionId: number) => {
    Events.app.navigate.emit({
      path: `${location.pathname}/sessions/session/${playSessionId}`,
      data: { tags: playlist?.tags },
    });
  };

  const { isCompletingSession } = useCompletePlaySession({
    delayMs: 500,
    onSuccess: (_, playSessionId) => {
      dispatch({ type: Actions.UsersActions.GetAccountInfo });
      navigateToSessionResults(playSessionId);
    },
  });

  useEffect(() => {
    Events.app.hideHeaderButton.emit();
  }, []);

  return (
    <ContainerWrapper id={PageId} enableBackButton sx={{ overflow: "visible" }}>
      <Container maxWidth={false}>
        <LegacyContentWrapper>
          <Grid container spacing={2} sx={{ paddingTop: theme.spacing(2) }}>
            <Grid item xs={7} sx={{ display: "flex" }}>
              <Paper data-shape="squircle" elevation={0} sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <BybImageSection imgSrc={playlist?.image_url} imgSize="large" sx={{ backgroundColor: theme.palette.dsSurface.v100 }} />
                <PlaylistDescription playlist={playlist} startSession={startSession} loading={loading} />
              </Paper>
            </Grid>

            <Grid item xs={5} sx={{ display: "flex" }}>
              <Stack spacing={2} sx={{ flexGrow: 1 }}>
                <PlaylistResult
                  score={bestResult?.results?.byb_iq}
                  date={bestResult?.play_session?.created_at}
                  loading={loading}
                  onViewAllResults={handleViewAllResults}
                  hasResults={Boolean(bestResult?.play_session?.id)}
                />
                <PlaylistPerformance loading={loading} currentStats={currentStats} previousStats={previousStats} />
              </Stack>
            </Grid>
          </Grid>
        </LegacyContentWrapper>
      </Container>

      <SessionResultsLoadingOverlay open={isCompletingSession} />
    </ContainerWrapper>
  );
};

export default Playlist;
